import { useEffect, useRef } from "react";
import styles from "./MouseFollower.module.css";

interface Props {
  images: string[];
  interval?: number;
}

const LERP = 0.1;

export default function MouseFollower({ images, interval = 1000 }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgARef = useRef<HTMLImageElement>(null);
  const imgBRef = useRef<HTMLImageElement>(null);

  const mouseRef = useRef({ x: 0, y: 0 });
  const posRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);
  const hasMovedRef = useRef(false);

  const activeSlotRef = useRef<"a" | "b">("a");
  const imgIdxRef = useRef(0);

  // 초기 src + opacity 설정
  useEffect(() => {
    if (!images.length) return;
    if (imgARef.current) {
      imgARef.current.src = images[0];
      imgARef.current.style.opacity = "1";
    }
    if (imgBRef.current) {
      imgBRef.current.src = images[1 % images.length];
      imgBRef.current.style.opacity = "0";
    }
    imgIdxRef.current = 0;
    activeSlotRef.current = "a";
  }, [images]);

  // mousemove 등록 + lerp 루프
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      if (!hasMovedRef.current) {
        posRef.current = { x: e.clientX, y: e.clientY };
        hasMovedRef.current = true;
        if (wrapperRef.current) wrapperRef.current.style.opacity = "1";
      }
    };

    const tick = () => {
      posRef.current.x += (mouseRef.current.x - posRef.current.x) * LERP;
      posRef.current.y += (mouseRef.current.y - posRef.current.y) * LERP;
      if (wrapperRef.current) {
        wrapperRef.current.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y}px)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // 이미지 주기적 전환 (크로스페이드)
  useEffect(() => {
    if (images.length <= 1) return;

    const id = setInterval(() => {
      const next = (imgIdxRef.current + 1) % images.length;

      if (activeSlotRef.current === "a") {
        if (imgBRef.current) {
          imgBRef.current.src = images[next];
          imgBRef.current.style.opacity = "1";
        }
        if (imgARef.current) imgARef.current.style.opacity = "0";
        activeSlotRef.current = "b";
      } else {
        if (imgARef.current) {
          imgARef.current.src = images[next];
          imgARef.current.style.opacity = "1";
        }
        if (imgBRef.current) imgBRef.current.style.opacity = "0";
        activeSlotRef.current = "a";
      }

      imgIdxRef.current = next;
    }, interval);

    return () => clearInterval(id);
  }, [images, interval]);

  if (!images.length) return null;

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div className={styles.inner}>
        <img ref={imgARef} alt="" className={styles.img} />
        <img ref={imgBRef} alt="" className={styles.img} />
      </div>
    </div>
  );
}
